import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { SubscriptionPauseType } from "api/subscriptionPauseApi";

dayjs.extend(utc);
dayjs.extend(timezone);

const KST = "Asia/Seoul";

export const PAUSE_DEFAULT_GUIDE =
  "일시정지는 잔여 기간과 횟수 내에서만 사용이 가능합니다.";

export type PauseButtonState = "active" | "reserved" | "reservable" | "hidden";

export function getRemainingDays(pause: SubscriptionPauseType): number {
  return pause.totalDays - pause.usedDays;
}

export function getRemainingCount(pause: SubscriptionPauseType): number {
  return pause.totalCount - pause.usedCount;
}

// 결제 페이지 일시정지 버튼 상태
// active(일시정지 중) > reserved(예약됨) > reservable(일시정지하기) > hidden(잔여 없음)
export function getPauseButtonState(
  pause: SubscriptionPauseType
): PauseButtonState {
  if (pause.reservation?.status === "active") return "active";
  if (pause.reservation?.status === "reserved") return "reserved";
  if (getRemainingDays(pause) > 0 && getRemainingCount(pause) > 0)
    return "reservable";
  return "hidden";
}

// 잔여 기준 소진 문구. 잔여가 모두 남아 있으면 null (기본 안내문 노출).
export function getExhaustMessage(pause: SubscriptionPauseType): string | null {
  const periodExhausted = getRemainingDays(pause) <= 0;
  const countExhausted = getRemainingCount(pause) <= 0;
  if (periodExhausted && countExhausted)
    return "현재 일시정지 기간과 횟수를 모두 소진하였습니다.";
  if (periodExhausted) return "현재 일시정지 기간을 모두 소진하였습니다.";
  if (countExhausted) return "현재 일시정지 횟수를 모두 소진하였습니다.";
  return null;
}

// 일시정지 기간 길이 (양끝 포함): 5/22 ~ 5/31 = 10일
export function durationDaysInclusive(start: Dayjs, end: Dayjs): number {
  return end.startOf("day").diff(start.startOf("day"), "day") + 1;
}

// "5월 22일"
export function formatKoreanMonthDay(d: Dayjs): string {
  return `${d.month() + 1}월 ${d.date()}일`;
}

// "5월 22일부터 5월 31일까지 10일간"
export function formatPausePeriodText(start: Dayjs, end: Dayjs): string {
  return `${formatKoreanMonthDay(start)}부터 ${formatKoreanMonthDay(
    end
  )}까지 ${durationDaysInclusive(start, end)}일간`;
}

// 오늘(KST) 기준 내일 자정 — 시작일 디폴트 & 시작일 minDate
export function getTomorrowKST(now: Dayjs = dayjs()): Dayjs {
  return now.tz(KST).startOf("day").add(1, "day");
}

// 만료일 전날 = 선택 가능한 마지막 일시정지 날.
// 일시정지 구간은 현재 구독 만료일보다 이전이어야 하므로 종료일 상한은 만료일−1일.
// expirationAt 미존재 시 undefined (만료 경계 제약 없음 — 잔여일로만 제한).
export function getLastPausableDay(expirationAt?: string): Dayjs | undefined {
  if (!expirationAt) return undefined;
  return dayjs(expirationAt).startOf("day").subtract(1, "day");
}

// 종료일 상한/디폴트 = min(시작일 + 가용일 - 1, 만료 경계)
export function getMaxEndDate(
  start: Dayjs,
  availableDays: number,
  lastPausableDay?: Dayjs
): Dayjs {
  const byBudget = start
    .startOf("day")
    .add(Math.max(availableDays - 1, 0), "day");
  if (lastPausableDay && lastPausableDay.startOf("day").isBefore(byBudget))
    return lastPausableDay.startOf("day");
  return byBudget;
}

// 종료일을 [시작일, 상한] 범위로 보정 (시작일 변경 시 호출)
export function clampEndDate(
  start: Dayjs,
  end: Dayjs,
  availableDays: number,
  lastPausableDay?: Dayjs
): Dayjs {
  const min = start.startOf("day");
  const max = getMaxEndDate(start, availableDays, lastPausableDay);
  const e = end.startOf("day");
  if (e.isBefore(min)) return min;
  if (e.isAfter(max)) return max;
  return e;
}
